import { commonQuotesService } from '@/services/quotes/common';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import {
  clearClonedQuoteDraftMark,
  getClonedQuoteDraftMeta,
} from '@/shared/utils/clone-quote-status.util';
import { useQuery } from '@tanstack/react-query';

/**
 * Status onde a marca local "clone draft" não vale mais — o estado real do
 * backend prevalece. Espelha a regra do `resolveQuoteListStatus` na lista
 * de cotações: se a cotação já avançou para análise ou caiu em um estado
 * terminal no Insuremo, ignoramos (e limpamos) a marca de clone draft.
 */
const STATUSES_THAT_IGNORE_CLONE_DRAFT = new Set<number>([
  PortalStatus.QuoteClosed,
  PortalStatus.QuoteExpired,
  PortalStatus.QuoteDeleted,
  PortalStatus.QuoteUnderAnalysis,
]);

export function useProposalDetails(policyId: string | undefined) {
  return useQuery({
    queryKey: ['proposal-details', policyId],
    queryFn: async () => {
      if (!policyId) {
        throw new Error('PolicyId é obrigatório');
      }

      const response = await commonQuotesService.getProposalDetails(policyId);
      if (!response.success || !response.data) {
        throw new Error(
          response.error || 'Erro ao carregar detalhes da proposta'
        );
      }

      const data = response.data;
      const clonedDraftMeta = getClonedQuoteDraftMeta(policyId);

      if (!clonedDraftMeta) {
        return data;
      }

      // Se o backend já moveu a cotação para análise (17) ou para um estado
      // terminal, o override de clone draft (que força QuoteInProgress) deixa
      // a UI fora de sincronia — ex.: o blur de "Em análise" no step de
      // cálculo nunca aparece. Limpa a marca e devolve o status real.
      const backendStatus = Number(data.statusPortal);
      if (
        Number.isFinite(backendStatus) &&
        STATUSES_THAT_IGNORE_CLONE_DRAFT.has(backendStatus)
      ) {
        clearClonedQuoteDraftMark(policyId);
        return data;
      }

      const toDateOnly = (value?: string) => {
        if (!value) return '';
        return value.split('T')[0] || value;
      };

      const maxIsoDate = (a: string, b: string) => {
        if (!a) return b;
        if (!b) return a;

        const ta = new Date(`${a}T12:00:00`).getTime();
        const tb = new Date(`${b}T12:00:00`).getTime();

        if (Number.isNaN(ta)) return b;
        if (Number.isNaN(tb)) return a;
        return ta >= tb ? a : b;
      };

      const addOneYear = (isoDate: string) => {
        if (!isoDate) return '';

        const base = new Date(`${isoDate}T12:00:00`);
        if (Number.isNaN(base.getTime())) return '';

        base.setFullYear(base.getFullYear() + 1);
        return base.toISOString().split('T')[0];
      };

      const clonedAt = clonedDraftMeta.clonedAt || new Date().toISOString();
      const clonedDateOnly = toDateOnly(clonedAt);
      const nextEffectiveDate = maxIsoDate(
        toDateOnly(data.effectiveDate),
        clonedDateOnly
      );
      const nextExpiryDate = addOneYear(nextEffectiveDate);

      return {
        ...data,
        quotationDate: clonedAt,
        proposalDate: clonedAt,
        effectiveDate: nextEffectiveDate,
        expiryDate: nextExpiryDate,
        terminateDate: nextExpiryDate,
        policyStatus: PortalStatus.QuoteInProgress,
        statusPortal: String(PortalStatus.QuoteInProgress),
      };
    },
    enabled: !!policyId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
}
